import React, { useEffect, useId, useMemo, useState } from 'react';
import { Box, Collapse, IconButton, Paper, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

function parseIndexList(raw) {
  if (!raw || !String(raw).trim()) return [];
  return String(raw)
    .split(/[,;\s]+/)
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n) && n >= 0);
}

function numOr(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function frameCountFromInputs(inputs) {
  const spacingsRaw = String(inputs.frame_spacings_m || '').trim();
  if (spacingsRaw) {
    const parts = spacingsRaw.split(/[,;\s]+/).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
    if (parts.length) return parts.length + 1;
  }
  const len = Math.max(Number(inputs.building_length_m) || 0, 0);
  const sp = Math.max(Number(inputs.frame_spacing_m) || 6, 0.1);
  return Math.max(2, Math.round(len / sp) + 1);
}

/** Axonometric: plan x = span, plan y = length, z = height. Yaw rotates plan 10° CW so roof diagonals separate from verticals. */
const ISO_PLAN_YAW_RAD = (10 * Math.PI) / 180;
const ISO_COS_YAW = Math.cos(ISO_PLAN_YAW_RAD);
const ISO_SIN_YAW = Math.sin(ISO_PLAN_YAW_RAD);

function isoPlanScreen(x, y, z) {
  const sx0 = (x - y) * 0.866;
  const sy0 = (x + y) * 0.5;
  return {
    sx: sx0 * ISO_COS_YAW - sy0 * ISO_SIN_YAW,
    sy: sx0 * ISO_SIN_YAW + sy0 * ISO_COS_YAW - z,
  };
}

function iso(x, y, z, cx, cy, scale) {
  const p = isoPlanScreen(x, y, z);
  return {
    sx: cx + p.sx * scale,
    sy: cy + p.sy * scale,
  };
}

function isoUnit(x, y, z) {
  return isoPlanScreen(x, y, z);
}

function boundsFromIsoPoints(points) {
  let minSx = Infinity;
  let maxSx = -Infinity;
  let minSy = Infinity;
  let maxSy = -Infinity;
  for (const p of points) {
    minSx = Math.min(minSx, p.sx);
    maxSx = Math.max(maxSx, p.sx);
    minSy = Math.min(minSy, p.sy);
    maxSy = Math.max(maxSy, p.sy);
  }
  return {
    minSx,
    maxSx,
    minSy,
    maxSy,
    contentW: Math.max(maxSx - minSx, 1),
    contentH: Math.max(maxSy - minSy, 1),
  };
}

/** Exact integers: no decimals; otherwise 2 decimals (exact within 0.01 → integer). */
function fmtM(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const r2 = Math.round(n * 100) / 100;
  if (Math.abs(r2 - Math.round(r2)) < 1e-9) return `${Math.round(r2)}`;
  return r2.toFixed(2);
}

function arrowHeadPoints(tipX, tipY, dirX, dirY, size = 5) {
  const len = Math.hypot(dirX, dirY) || 1;
  const ux = dirX / len;
  const uy = dirY / len;
  const px = -uy;
  const py = ux;
  const back = size;
  const half = size * 0.45;
  return [
    `${tipX},${tipY}`,
    `${tipX - ux * back + px * half},${tipY - uy * back + py * half}`,
    `${tipX - ux * back - px * half},${tipY - uy * back - py * half}`,
  ].join(' ');
}

/**
 * Top-chord polyline in local truss coords: x along span, y above bottom chord.
 * Mirrors backend truss_geometry._resolve_dual_profile / _bottom_top_nodes silhouette.
 */
function archSegmentCount(inputs) {
  return Math.max(8, Math.min(32, Math.round(numOr(inputs.arch_segments, 16))));
}

function archRise(inputs) {
  return Math.max(numOr(inputs.arch_rise_m, inputs.height_left_m), 0.2);
}

function isParabolicArch(inputs) {
  const curve = String(inputs.arch_curve || 'circular').toLowerCase();
  return curve === 'parabolic' || curve === 'parabola';
}

function capNormalDepth(span, rise, parabolic, depth) {
  const h = Math.max(depth, 0.05);
  if (parabolic) {
    const rho = (span * span) / (8 * rise);
    return Math.min(h, 0.45 * rho, 0.85 * rise);
  }
  const half = span / 2;
  const radius = (half * half + rise * rise) / (2 * rise);
  return Math.min(h, 0.45 * radius, 0.85 * rise);
}

function circleStations(span, rise, n) {
  const half = span / 2;
  const radius = (half * half + rise * rise) / (2 * rise);
  const cy = rise - radius;
  const left = Math.atan2(0 - cy, 0 - half);
  const right = Math.atan2(0 - cy, span - half);
  const crown = Math.atan2(rise - cy, 0);
  let sweep = right - left;
  const alt = sweep >= 0 ? sweep - 2 * Math.PI : sweep + 2 * Math.PI;
  const mid = (candidate) => left + 0.5 * candidate;
  const err = (ang) => Math.abs(((ang - crown + Math.PI) % (2 * Math.PI)) - Math.PI);
  if (err(mid(alt)) < err(mid(sweep))) sweep = alt;
  const pts = [];
  for (let i = 0; i <= n; i += 1) {
    const ang = left + sweep * (i / n);
    const x = half + radius * Math.cos(ang);
    const y = cy + radius * Math.sin(ang);
    pts.push({ x, y, nx: (half - x) / radius, ny: (cy - y) / radius });
  }
  return pts;
}

function parabolaStations(span, rise, n) {
  const yAt = (x) => (4 * rise * x * (span - x)) / (span * span);
  const slopeAt = (x) => (4 * rise * (span - 2 * x)) / (span * span);
  const nFine = 400;
  const xs = [];
  const ys = [];
  for (let i = 0; i <= nFine; i += 1) {
    const x = (span * i) / nFine;
    xs.push(x);
    ys.push(yAt(x));
  }
  const cum = [0];
  for (let i = 0; i < nFine; i += 1) {
    cum.push(cum[i] + Math.hypot(xs[i + 1] - xs[i], ys[i + 1] - ys[i]));
  }
  const total = cum[nFine];
  const pts = [];
  let k = 0;
  for (let i = 0; i <= n; i += 1) {
    const target = (total * i) / n;
    while (k < nFine - 1 && cum[k + 1] < target) k += 1;
    const spanC = cum[k + 1] - cum[k];
    const t = spanC <= 1e-15 ? 0 : Math.max(0, Math.min(1, (target - cum[k]) / spanC));
    const x = i === 0 ? 0 : i === n ? span : xs[k] + t * (xs[k + 1] - xs[k]);
    const y = yAt(x);
    const slope = slopeAt(x);
    const norm = Math.hypot(slope, 1);
    pts.push({ x, y, nx: slope / norm, ny: -1 / norm });
  }
  return pts;
}

function archStations(inputs, span) {
  const rise = archRise(inputs);
  const n = archSegmentCount(inputs);
  return isParabolicArch(inputs) && (inputs.arch_system || 'beam') === 'truss'
    ? parabolaStations(span, rise, n)
    : circleStations(span, rise, n);
}

function archLocalPolyline(inputs, span) {
  return archStations(inputs, span).map((p) => ({ x: p.x, y: p.y }));
}

/** Top chord and the parallel inner chord, y measured above the top springing. */
function archTrussPolylines(inputs, span) {
  const rise = archRise(inputs);
  const parabolic = isParabolicArch(inputs);
  const depth = capNormalDepth(span, rise, parabolic, numOr(inputs.arch_depth_m, 0.8));
  const stations = archStations(inputs, span);
  return {
    top: stations.map((p) => ({ x: p.x, y: p.y })),
    bottom: stations.map((p) => ({ x: p.x + depth * p.nx, y: p.y + depth * p.ny })),
  };
}

function topChordLocalPolyline(inputs, span) {
  const shape = inputs.shape || 'flat';
  const hL = Math.max(numOr(inputs.height_left_m, 1.2), 0);
  const hR = numOr(inputs.height_right_m, 0);
  if (shape === 'arch') return archLocalPolyline(inputs, span);
  const ridgeOffset = numOr(inputs.ridge_offset_m, 0);

  if (shape === 'mono') {
    return [
      { x: 0, y: hL },
      { x: span, y: Math.max(hR, 0) },
    ];
  }

  if (shape === 'dual') {
    const hRidgeRaw = inputs.dual_ridge_height_m;
    const hRidge =
      hRidgeRaw !== '' && hRidgeRaw != null && Number.isFinite(Number(hRidgeRaw))
        ? Math.max(Number(hRidgeRaw), 0)
        : Math.max(hL, Math.max(hR, 0));
    const ridgeX = Math.max(1e-6, Math.min(span - 1e-6, span / 2 + ridgeOffset));
    return [
      { x: 0, y: hL },
      { x: ridgeX, y: hRidge },
      { x: span, y: Math.max(hR, 0) },
    ];
  }

  // flat
  return [
    { x: 0, y: hL },
    { x: span, y: hL },
  ];
}

function bottomZAtX(x, span, colL, colR) {
  if (span <= 1e-9) return colL;
  const t = Math.max(0, Math.min(1, x / span));
  return colL + t * (colR - colL);
}

function chordSegmentLengths(pts) {
  const lengths = [];
  for (let i = 0; i < pts.length - 1; i += 1) {
    const dx = pts[i + 1].x - pts[i].x;
    const dy = pts[i + 1].y - pts[i].y;
    lengths.push(Math.hypot(dx, dy));
  }
  return lengths;
}

/**
 * Engineering dimension: extension lines, arrowheads, value outside the line.
 * Pass `outward` (screen-space vector) so the dim always sits outside the building.
 */
function DimensionAnnotation({
  a,
  b,
  label,
  offset = 28,
  color = '#212121',
  fontSize = 10,
  outward = null,
  labelGap = 7,
}) {
  const dx = b.sx - a.sx;
  const dy = b.sy - a.sy;
  const len = Math.hypot(dx, dy) || 1;
  let nx = -dy / len;
  let ny = dx / len;
  if (outward) {
    const ox = outward.sx ?? outward.x ?? 0;
    const oy = outward.sy ?? outward.y ?? 0;
    if (nx * ox + ny * oy < 0) {
      nx = -nx;
      ny = -ny;
    }
  } else if (ny < 0 || (Math.abs(ny) < 1e-6 && nx > 0)) {
    nx = -nx;
    ny = -ny;
  }

  const gap = 2;
  const overshoot = 3;
  const d = Math.abs(offset);

  const ext1Start = { sx: a.sx + nx * gap, sy: a.sy + ny * gap };
  const ext2Start = { sx: b.sx + nx * gap, sy: b.sy + ny * gap };
  const p1 = { sx: a.sx + nx * d, sy: a.sy + ny * d };
  const p2 = { sx: b.sx + nx * d, sy: b.sy + ny * d };
  const ext1End = { sx: a.sx + nx * (d + overshoot), sy: a.sy + ny * (d + overshoot) };
  const ext2End = { sx: b.sx + nx * (d + overshoot), sy: b.sy + ny * (d + overshoot) };

  const mid = { sx: (p1.sx + p2.sx) / 2, sy: (p1.sy + p2.sy) / 2 };
  const labelPos = { sx: mid.sx + nx * labelGap, sy: mid.sy + ny * labelGap };

  return (
    <g>
      <line
        x1={ext1Start.sx}
        y1={ext1Start.sy}
        x2={ext1End.sx}
        y2={ext1End.sy}
        stroke={color}
        strokeWidth="0.75"
      />
      <line
        x1={ext2Start.sx}
        y1={ext2Start.sy}
        x2={ext2End.sx}
        y2={ext2End.sy}
        stroke={color}
        strokeWidth="0.75"
      />
      <line x1={p1.sx} y1={p1.sy} x2={p2.sx} y2={p2.sy} stroke={color} strokeWidth="1" />
      <polygon points={arrowHeadPoints(p1.sx, p1.sy, p1.sx - p2.sx, p1.sy - p2.sy)} fill={color} />
      <polygon points={arrowHeadPoints(p2.sx, p2.sy, p2.sx - p1.sx, p2.sy - p1.sy)} fill={color} />
      <text
        x={labelPos.sx}
        y={labelPos.sy}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={fontSize}
        fill={color}
      >
        {label}
      </text>
    </g>
  );
}

function linkStroke(group) {
  if (group === 'lateral_support_longitudinal') return { stroke: '#e91e63', width: 2.4 };
  if (group === 'roof_collector_longitudinal') return { stroke: '#2e7d32', width: 2.2 };
  if (group === 'roof_brace_longitudinal') return { stroke: '#1b5e20', width: 1.4 };
  if (group === 'roof_collector_transverse') return { stroke: '#1565c0', width: 2 };
  if (group === 'roof_brace_transverse') return { stroke: '#0d47a1', width: 1.4 };
  if (group === 'vertical_brace_gable') return { stroke: '#ef6c00', width: 2.2 };
  if (group === 'vertical_brace_end') return { stroke: '#ef6c00', width: 2.2 };
  if (group === 'vertical_brace_sidewall') return { stroke: '#e65100', width: 1.6 };
  if (group === 'wind_column') return { stroke: '#7b1fa2', width: 1.8 };
  return { stroke: '#757575', width: 1.2 };
}

export default function BuildingIsometricView({
  inputs,
  title = 'Building layout',
  geometry = null,
  bracingLayout = null,
  collapsible = false,
  defaultExpanded = true,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  useEffect(() => {
    if (collapsible) setExpanded(defaultExpanded);
  }, [collapsible, defaultExpanded]);

  const gradId = useId();
  const numFrames = frameCountFromInputs(inputs);
  const span = Math.max(Number(inputs.span_m) || 12, 1);
  const isArch = (inputs.shape || '') === 'arch';
  const isTrussArch = isArch && (inputs.arch_system || 'beam') === 'truss';
  const springH = Math.max(numOr(inputs.arch_springing_height_m, 0), 0);
  const archPair = useMemo(
    () => (isTrussArch ? archTrussPolylines(inputs, span) : null),
    [isTrussArch, inputs, span],
  );
  const springDrop = archPair ? archPair.top[0].y - archPair.bottom[0].y : 0;
  const outerSpringZ = isTrussArch ? Math.max(springH, springDrop) : springH;
  const colL = isArch ? (isTrussArch ? outerSpringZ : Math.max(springH, 0.05)) : Math.max(numOr(inputs.column_left_m, 4), 0.5);
  const colR = isArch ? (isTrussArch ? outerSpringZ : Math.max(springH, 0.05)) : Math.max(numOr(inputs.column_right_m, 4), 0.5);
  const maxCol = Math.max(colL, colR);

  const spacings = useMemo(() => {
    const raw = String(inputs.frame_spacings_m || '').trim();
    if (raw) {
      const parts = raw.split(/[,;\s]+/).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
      if (parts.length === numFrames - 1) return parts;
    }
    const sp = Math.max(Number(inputs.frame_spacing_m) || 6, 0.1);
    return Array.from({ length: Math.max(numFrames - 1, 1) }, () => sp);
  }, [inputs.frame_spacings_m, inputs.frame_spacing_m, numFrames]);

  const length = spacings.reduce((a, b) => a + b, 0);
  const transverseBraced = new Set(
    parseIndexList(inputs.transverse_brace_frame_indices || inputs.vertical_bracing_bays),
  );
  const lengthwiseBraced = new Set(parseIndexList(inputs.lengthwise_brace_frame_indices));

  const frameY = useMemo(() => {
    const ys = [0];
    let acc = 0;
    for (const s of spacings) {
      acc += s;
      ys.push(acc);
    }
    return ys.slice(0, numFrames);
  }, [spacings, numFrames]);

  /** Silhouette for roof planes / dimensions (heels + ridge). */
  const topSilhouette = useMemo(() => topChordLocalPolyline(inputs, span), [inputs, span]);
  const innerLocal = archPair ? archPair.bottom : null;

  /**
   * Building overview uses the input silhouette so dual/mono heels stay correct.
   * Preview mesh can add intermediate top-chord stations when it matches the same end/ridge heights.
   */
  const topLocal = useMemo(() => {
    if ((inputs.shape || '') === 'arch') return topSilhouette;
    const nodes = geometry?.nodes;
    const members = geometry?.members;
    if (!nodes?.length || !members?.length) return topSilhouette;

    const byId = new Map(nodes.map((n) => [n.id, n]));
    const topGroups = new Set(['top_chord', 'top_chord_left', 'top_chord_right']);
    const topPts = [];
    for (const m of members) {
      if (!topGroups.has(m.group)) continue;
      const ni = byId.get(m.i);
      const nj = byId.get(m.j);
      if (!ni || !nj) continue;
      topPts.push({ x: ni.x, y: ni.y }, { x: nj.x, y: nj.y });
    }
    if (topPts.length < 2) return topSilhouette;

    const byX = new Map();
    for (const p of topPts) {
      const key = Math.round(p.x * 1e4) / 1e4;
      const prev = byX.get(key);
      if (!prev || p.y > prev.y) byX.set(key, { x: p.x, y: p.y });
    }
    const sorted = [...byX.values()].sort((a, b) => a.x - b.x);
    if (sorted.length < 2) return topSilhouette;

    const bottomYs = nodes.filter((n) => n.role === 'bottom_chord').map((n) => n.y);
    const eaveRef = bottomYs.length ? Math.min(...bottomYs) : 0;
    const mesh = sorted.map((p) => ({ x: p.x, y: p.y - eaveRef }));

    // Reject stale mesh that still has zero-depth dual heels while inputs ask for nonzero ends
    const silL = topSilhouette[0]?.y ?? 0;
    const silR = topSilhouette[topSilhouette.length - 1]?.y ?? 0;
    const meshL = mesh[0]?.y ?? 0;
    const meshR = mesh[mesh.length - 1]?.y ?? 0;
    if (Math.abs(silL - meshL) > 0.05 || Math.abs(silR - meshR) > 0.05) {
      return topSilhouette;
    }
    // Ensure ridge peak from silhouette is present
    if (topSilhouette.length >= 3) {
      const ridge = topSilhouette[1];
      const hasRidge = mesh.some((p) => Math.abs(p.x - ridge.x) < 0.05 && Math.abs(p.y - ridge.y) < 0.05);
      if (!hasRidge) {
        return [...mesh, ridge].sort((a, b) => a.x - b.x);
      }
    }
    return mesh;
  }, [geometry, topSilhouette]);

  const topChordSlopeLengths = useMemo(() => chordSegmentLengths(topSilhouette), [topSilhouette]);
  const totalTrussChordLen = topChordSlopeLengths.reduce((a, b) => a + b, 0);

  const ridgeLocal = useMemo(() => {
    if (!topSilhouette.length) return null;
    let best = topSilhouette[0];
    for (const p of topSilhouette) {
      if (p.y > best.y) best = p;
    }
    if (
      topSilhouette.length >= 3 &&
      best.y > Math.max(topSilhouette[0].y, topSilhouette[topSilhouette.length - 1].y) + 1e-6
    ) {
      return best;
    }
    if ((inputs.shape || 'flat') === 'dual') return best;
    return null;
  }, [topSilhouette, inputs.shape]);

  const maxRoofZ = useMemo(() => {
    let zMax = maxCol;
    for (const p of topLocal) {
      zMax = Math.max(zMax, bottomZAtX(p.x, span, colL, colR) + p.y);
    }
    return zMax;
  }, [topLocal, span, colL, colR, maxCol]);

  const vbW = 580;
  const vbH = 360;

  const { scale, cx, cy } = useMemo(() => {
    const samplePoints = [];
    const add = (x, y, z) => {
      samplePoints.push(isoUnit(x, y, z));
    };

    // Footprint + room for stacked dimensions outside the building
    const dimPadY = Math.max(length * 0.22, 3.5);
    const dimPadX = Math.max(span * 0.22, 3.5);
    add(-dimPadX, -dimPadY, 0);
    add(span + dimPadX, -dimPadY, 0);
    add(span + dimPadX, length + dimPadY, 0);
    add(-dimPadX, length + dimPadY, 0);

    for (const fy of frameY) {
      add(0, fy, 0);
      add(span, fy, 0);
      add(0, fy, colL);
      add(span, fy, colR);
      for (const p of topLocal) {
        add(p.x, fy, bottomZAtX(p.x, span, colL, colR) + p.y);
      }
      if (innerLocal) {
        for (const p of innerLocal) {
          add(p.x, fy, bottomZAtX(p.x, span, colL, colR) + p.y);
        }
      }
    }

    const bounds = boundsFromIsoPoints(samplePoints);
    const pad = 0.08;
    const fitScale = Math.min(
      (vbW * (1 - 2 * pad)) / bounds.contentW,
      (vbH * (1 - 2 * pad)) / bounds.contentH,
      18,
    );
    const centerSx = ((bounds.minSx + bounds.maxSx) / 2) * fitScale;
    const centerSy = ((bounds.minSy + bounds.maxSy) / 2) * fitScale;
    return {
      scale: fitScale,
      cx: vbW / 2 - centerSx,
      cy: vbH * 0.52 - centerSy,
    };
  }, [span, length, frameY, colL, colR, topLocal, innerLocal, vbW, vbH]);

  const project = (x, y, z) => iso(x, y, z, cx, cy, scale);

  /** Screen-space outward hint from a world point toward the exterior. */
  const outwardAt = (x, y, z, dx, dy, dz = 0) => {
    const p0 = project(x, y, z);
    const p1 = project(x + dx, y + dy, z + dz);
    return { sx: p1.sx - p0.sx, sy: p1.sy - p0.sy };
  };

  // Right wall (+x): lengthwise bay + overall. Far gable (+y): span in front of building.
  const outRight = outwardAt(span, length * 0.5, 0, 1, 0, 0);
  const outFar = outwardAt(span * 0.5, length, 0, 0, 1, 0);
  const outUp = outwardAt(span * 0.5, 0, maxRoofZ, 0, 0, 1);

  const frames = frameY.map((fy, idx) => {
    const baseL = project(0, fy, 0);
    const baseR = project(span, fy, 0);
    const eaveL = project(0, fy, colL);
    const eaveR = project(span, fy, colR);
    const bottomPts = [eaveL, eaveR];
    const topPts = topLocal.map((p) =>
      project(p.x, fy, bottomZAtX(p.x, span, colL, colR) + p.y),
    );
    const innerPts = innerLocal
      ? innerLocal.map((p) => project(p.x, fy, bottomZAtX(p.x, span, colL, colR) + p.y))
      : null;
    const ridge =
      ridgeLocal != null
        ? project(ridgeLocal.x, fy, bottomZAtX(ridgeLocal.x, span, colL, colR) + ridgeLocal.y)
        : null;
    return {
      idx,
      fy,
      baseL,
      baseR,
      eaveL,
      eaveR,
      bottomPts: innerPts || bottomPts,
      topPts,
      innerPts,
      ridge,
    };
  });

  const bracingLinks = bracingLayout?.links ?? [];
  const layoutMode = bracingLayout?.layout_mode || inputs.bracing_layout_mode || 'auto';

  const bracingSegments = useMemo(() => {
    if (!bracingLinks.length) return [];
    return bracingLinks.map((lk) => {
      const p0 = iso(lk.x0_m, lk.y0_m, lk.z0_m, cx, cy, scale);
      const p1 = iso(lk.x1_m, lk.y1_m, lk.z1_m, cx, cy, scale);
      const style = linkStroke(lk.group);
      return {
        key: lk.link_id,
        p0,
        p1,
        style,
        source: lk.source,
        group: lk.group,
      };
    });
  }, [bracingLinks, cx, cy, scale]);

  const roofLongitudinal = [];
  for (let i = 0; i < frames.length - 1; i += 1) {
    const a = frames[i];
    const b = frames[i + 1];
    const braced =
      transverseBraced.has(i) ||
      transverseBraced.has(i + 1) ||
      lengthwiseBraced.has(i) ||
      lengthwiseBraced.has(i + 1);
    // Left eave, right eave, ridge (if any)
    roofLongitudinal.push({
      key: `eaveL-${i}`,
      a: a.eaveL,
      b: b.eaveL,
      braced,
    });
    roofLongitudinal.push({
      key: `eaveR-${i}`,
      a: a.eaveR,
      b: b.eaveR,
      braced,
    });
    if (a.ridge && b.ridge) {
      roofLongitudinal.push({
        key: `ridge-${i}`,
        a: a.ridge,
        b: b.ridge,
        braced,
        ridge: true,
      });
    }
  }

  // Uniform bay? show one spacing label style; else label each
  const uniformBay = spacings.every((s) => Math.abs(s - spacings[0]) < 1e-6);
  const svgKey = `${span}-${length}-${numFrames}-${inputs.shape}-${inputs.arch_system || ''}-${inputs.arch_curve || ''}-${colL}-${colR}-${topLocal.map((p) => `${p.x}:${p.y}`).join('|')}-${(innerLocal || []).map((p) => `${p.x}:${p.y}`).join('|')}`;

  const near = frames[0];
  const far = frames[frames.length - 1];
  const isFlat = (inputs.shape || 'flat') === 'flat';
  // Color / axis legend (kept for maintainers; not shown in UI):
  // Frame indices 0…N-1 (near → far gable). Building axes: X length (frames), Y span, Z up.
  // Brown: columns (base→top chord) · Blue: bottom chord · Red: top chord (flat/mono/dual).
  // Pink: lateral support · Green: roof HT (Y-dir) · Blue: transverse HT at VB bay ·
  // Orange: vertical VB (X-dir sidewall @ top chord; Y-dir gable @ bottom chord) · Purple: wind columns.
  // Bracing layout follows layoutMode.

  const body = (
    <>
      <Box sx={{ width: '100%', overflow: 'auto' }}>
        <svg
          key={svgKey}
          viewBox={`0 0 ${vbW} ${vbH}`}
          width="100%"
          style={{ maxHeight: 340, display: 'block' }}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#e3f2fd" />
              <stop offset="100%" stopColor="#fafafa" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width={vbW} height={vbH} fill={`url(#${gradId})`} rx="4" />

          {/* Ground footprint */}
          <polygon
            points={[
              project(0, 0, 0),
              project(span, 0, 0),
              project(span, length, 0),
              project(0, length, 0),
            ]
              .map((p) => `${p.sx},${p.sy}`)
              .join(' ')}
            fill="rgba(144,164,174,0.08)"
            stroke="#90a4ae"
            strokeWidth="1"
            strokeDasharray="4 3"
          />

          {/* Building axis triad at near-left corner (X=length, Y=span, Z=up) */}
          {(() => {
            const origin = project(0, 0, 0);
            const axisLen = Math.max(span, length) * 0.14;
            const zLen = Math.max(maxCol, 1) * 0.35;
            const xEnd = project(0, axisLen, 0);
            const yEnd = project(axisLen, 0, 0);
            const zEnd = project(0, 0, zLen);
            const axes = [
              { key: 'X', from: origin, to: xEnd, color: '#c62828', label: 'X' },
              { key: 'Y', from: origin, to: yEnd, color: '#1565c0', label: 'Y' },
              { key: 'Z', from: origin, to: zEnd, color: '#2e7d32', label: 'Z' },
            ];
            return (
              <g aria-label="Building axes">
                {axes.map((ax) => {
                  const dx = ax.to.sx - ax.from.sx;
                  const dy = ax.to.sy - ax.from.sy;
                  const labelPos = {
                    sx: ax.to.sx + dx * 0.12,
                    sy: ax.to.sy + dy * 0.12,
                  };
                  return (
                    <g key={ax.key}>
                      <line
                        x1={ax.from.sx}
                        y1={ax.from.sy}
                        x2={ax.to.sx}
                        y2={ax.to.sy}
                        stroke={ax.color}
                        strokeWidth="1.6"
                      />
                      <polygon
                        points={arrowHeadPoints(ax.to.sx, ax.to.sy, dx, dy, 4)}
                        fill={ax.color}
                      />
                      <text
                        x={labelPos.sx}
                        y={labelPos.sy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="10"
                        fontWeight="700"
                        fill={ax.color}
                      >
                        {ax.label}
                      </text>
                    </g>
                  );
                })}
              </g>
            );
          })()}

          {/* Longitudinal roof edges (legacy highlight when no bracing_layout) */}
          {!bracingSegments.length &&
            roofLongitudinal.map((e) => (
              <line
                key={e.key}
                x1={e.a.sx}
                y1={e.a.sy}
                x2={e.b.sx}
                y2={e.b.sy}
                stroke={e.braced ? '#2e7d32' : e.ridge ? '#90a4ae' : '#b0bec5'}
                strokeWidth={e.braced ? 2.2 : e.ridge ? 1.2 : 1}
                strokeDasharray={e.braced ? undefined : '3 2'}
              />
            ))}

          {/* Two-way bracing links (existing truss nodes only) */}
          {bracingSegments.map((seg) => (
            <line
              key={seg.key}
              x1={seg.p0.sx}
              y1={seg.p0.sy}
              x2={seg.p1.sx}
              y2={seg.p1.sy}
              stroke={seg.style.stroke}
              strokeWidth={seg.style.width}
              strokeDasharray={seg.source === 'manual' ? '5 3' : undefined}
              opacity={seg.group.includes('brace') ? 0.92 : 0.85}
            />
          ))}

          {/* Frames: columns + truss chords */}
          {frames.map(({ idx, baseL, baseR, eaveL, eaveR, bottomPts, topPts, innerPts }) => (
            <g key={`frame-${idx}`}>
              {/* Columns: base → bottom chord → top chord (matches analysis model) */}
              <line x1={baseL.sx} y1={baseL.sy} x2={eaveL.sx} y2={eaveL.sy} stroke="#5d4037" strokeWidth="2" />
              {topPts.length >= 1 && (
                <line
                  x1={eaveL.sx}
                  y1={eaveL.sy}
                  x2={topPts[0].sx}
                  y2={topPts[0].sy}
                  stroke="#5d4037"
                  strokeWidth="2"
                />
              )}
              <line x1={baseR.sx} y1={baseR.sy} x2={eaveR.sx} y2={eaveR.sy} stroke="#5d4037" strokeWidth="2" />
              {topPts.length >= 1 && (
                <line
                  x1={eaveR.sx}
                  y1={eaveR.sy}
                  x2={topPts[topPts.length - 1].sx}
                  y2={topPts[topPts.length - 1].sy}
                  stroke="#5d4037"
                  strokeWidth="2"
                />
              )}

              {/* Bottom chord. A trussed arch uses the inner parallel curve, closed by end posts. */}
              <polyline
                points={bottomPts.map((p) => `${p.sx},${p.sy}`).join(' ')}
                fill="none"
                stroke="#1565c0"
                strokeWidth="1.4"
              />
              {innerPts && topPts.length >= 1 && innerPts.length >= 1 && (
                <>
                  <line
                    x1={topPts[0].sx}
                    y1={topPts[0].sy}
                    x2={innerPts[0].sx}
                    y2={innerPts[0].sy}
                    stroke="#1565c0"
                    strokeWidth="1.4"
                  />
                  <line
                    x1={topPts[topPts.length - 1].sx}
                    y1={topPts[topPts.length - 1].sy}
                    x2={innerPts[innerPts.length - 1].sx}
                    y2={innerPts[innerPts.length - 1].sy}
                    stroke="#1565c0"
                    strokeWidth="1.4"
                  />
                </>
              )}

              {/* Top chord (roof shape) */}
              <polyline
                points={topPts.map((p) => `${p.sx},${p.sy}`).join(' ')}
                fill="none"
                stroke="#c62828"
                strokeWidth="1.6"
              />

              <circle cx={baseL.sx} cy={baseL.sy} r="9" fill="#fff" stroke="#1565c0" strokeWidth="1" />
              <text x={baseL.sx} y={baseL.sy + 3.5} textAnchor="middle" fontSize="10" fill="#1565c0" fontWeight="600">
                {idx}
              </text>
            </g>
          ))}

          {/* Lengthwise column–column dims OUTSIDE on the right-most columns (x=span) */}
          {spacings.map((sp, i) => {
            const y0 = frameY[i];
            const y1 = frameY[i + 1];
            if (y0 == null || y1 == null) return null;
            return (
              <DimensionAnnotation
                key={`bay-dim-right-${i}`}
                a={project(span, y0, 0)}
                b={project(span, y1, 0)}
                label={fmtM(sp)}
                offset={40}
                outward={outRight}
                fontSize={uniformBay ? 10 : 9}
              />
            );
          })}

          {/* Overall building length — stacked further outside on the right */}
          <DimensionAnnotation
            a={project(span, 0, 0)}
            b={project(span, length, 0)}
            label={fmtM(length)}
            offset={62}
            outward={outRight}
          />

          {/* Span (truss column–column) OUTSIDE at last frame — in front of the building (bottom-left) */}
          {far && (
            <DimensionAnnotation
              a={far.baseL}
              b={far.baseR}
              label={fmtM(span)}
              offset={40}
              outward={outFar}
            />
          )}

          {/* Roof-plane lengths on frame 0 truss (top-right), above the chords */}
          {near &&
            topSilhouette.length >= 2 &&
            topChordSlopeLengths.map((segLen, i) => {
              const p0 = topSilhouette[i];
              const p1 = topSilhouette[i + 1];
              if (!p0 || !p1) return null;
              const fy = 0;
              const a = project(p0.x, fy, bottomZAtX(p0.x, span, colL, colR) + p0.y);
              const b = project(p1.x, fy, bottomZAtX(p1.x, span, colL, colR) + p1.y);
              return (
                <DimensionAnnotation
                  key={`truss-len-${i}`}
                  a={a}
                  b={b}
                  label={fmtM(segLen)}
                  offset={28}
                  outward={outUp}
                  color="#b71c1c"
                  fontSize={9}
                />
              );
            })}

          {/* Flat truss height — outside right of frame 0 */}
          {isFlat && near && topSilhouette[0] && (
            <DimensionAnnotation
              a={near.eaveR}
              b={project(span, near.fy, colR + topSilhouette[topSilhouette.length - 1].y)}
              label={fmtM(topSilhouette[0].y)}
              offset={32}
              labelGap={16}
              outward={outRight}
              color="#6a1b9a"
              fontSize={9}
            />
          )}

          <text x={12} y={18} fontSize="10" fill="#546e7a">
            {numFrames} frames · spacing {uniformBay ? fmtM(spacings[0]) : 'variable'} m · top chord{' '}
            {fmtM(totalTrussChordLen)} m
            {maxRoofZ > maxCol + 0.05 ? ` · ridge +${fmtM(maxRoofZ - maxCol)} m` : ''}
          </text>
        </svg>
      </Box>
    </>
  );

  return (
    <Paper
      sx={{
        p: collapsible && !expanded ? 0.5 : 1.5,
        px: collapsible && !expanded ? 1 : 1.5,
        mb: collapsible && !expanded ? 1 : 2,
      }}
    >
      {collapsible ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: expanded ? 0.5 : 0,
            minHeight: expanded ? undefined : 28,
          }}
        >
          <Typography variant={expanded ? 'subtitle2' : 'caption'} sx={{ fontWeight: expanded ? 600 : 500 }}>
            {title}
            {!expanded && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {numFrames} frames · {uniformBay ? fmtM(spacings[0]) : 'var.'} m · {layoutMode}
              </Typography>
            )}
          </Typography>
          <IconButton
            size="small"
            aria-label={expanded ? 'Collapse building layout' : 'Expand building layout'}
            onClick={() => setExpanded((open) => !open)}
            sx={{ p: 0.25 }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      ) : (
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
      )}
      {collapsible ? <Collapse in={expanded}>{body}</Collapse> : body}
    </Paper>
  );
}
